import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Mail, Phone, MapPin, MessageCircle, Send, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SiteFooter from "@/components/site/SiteFooter";

/**
 * Public "Contact us" page. Currently the form is a mailto: link — a real inbox
 * endpoint can be wired later without redesign.
 */
export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast.error("Please add your email and a message");
      return;
    }
    setBusy(true);
    // Compose a mailto: URL. Backend endpoint is a future enhancement.
    const body = `From: ${form.name || "(anonymous)"} <${form.email}>\nPhone: ${form.phone || "—"}\n\n${form.message}`;
    const href = `mailto:partnerships@ranaassociate.com?subject=${encodeURIComponent(form.subject || "Website enquiry")}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    setTimeout(() => {
      setBusy(false);
      toast.success("Your email client will open. If not, write to partnerships@ranaassociate.com directly.");
    }, 800);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-slate-950 text-white flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-slate-950">Rana Associates</div>
              <div className="text-xs text-slate-500">Pan-Rajasthan · Property Valuation</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-950" data-testid="contact-back-home">
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Get in touch</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-slate-950">Talk to our onboarding team</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Banks, NBFCs and independent valuers — reach us for partnerships, pricing or platform demos. We usually reply within 24 business hours.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: contact info */}
          <aside className="space-y-4">
            <InfoCard icon={<MapPin className="h-5 w-5" />} title="Head office" testId="ci-office">
              Beawar, Rajasthan<br />India — 305901
            </InfoCard>
            <InfoCard icon={<Phone className="h-5 w-5" />} title="Call" testId="ci-phone">
              <a href="tel:+919782519708" className="hover:text-amber-700">+91 97825 19708</a><br />
              <a href="tel:+919166363761" className="hover:text-amber-700">+91 91663 63761</a><br />
              <span className="text-xs text-slate-500">Mon–Sat · 9 AM – 8 PM IST</span>
            </InfoCard>
            <InfoCard icon={<Mail className="h-5 w-5" />} title="Email" testId="ci-email">
              <a href="mailto:partnerships@ranaassociate.com" className="hover:text-amber-700">partnerships@ranaassociate.com</a><br />
              <a href="mailto:support@ranaassociate.com" className="hover:text-amber-700">support@ranaassociate.com</a>
            </InfoCard>
            <InfoCard icon={<MessageCircle className="h-5 w-5" />} title="WhatsApp" testId="ci-whatsapp">
              <a href="https://wa.me/919782519708" target="_blank" rel="noreferrer noopener" className="hover:text-amber-700">Chat on WhatsApp</a>
            </InfoCard>
          </aside>

          {/* Right: form */}
          <form onSubmit={submit} className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name">Your name</Label>
                <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="c-email">Work email *</Label>
                <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="contact-email" />
              </div>
              <div>
                <Label htmlFor="c-phone">Phone</Label>
                <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="contact-phone-input" />
              </div>
              <div>
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Onboard our bank" className="mt-1.5" data-testid="contact-subject" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="c-msg">Message *</Label>
                <Textarea id="c-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" placeholder="Tell us about your bank / NBFC and the districts you cover…" data-testid="contact-message" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto" data-testid="contact-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-2" /> Send message</>)}
                </Button>
                <p className="mt-3 text-xs text-slate-500">
                  By submitting, you agree to our <Link to="/terms" className="underline hover:text-slate-900">Terms</Link> and <Link to="/privacy" className="underline hover:text-slate-900">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </form>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function InfoCard({ icon, title, children, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testId}>
      <div className="h-9 w-9 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center mb-3">{icon}</div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">{title}</div>
      <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
    </div>
  );
}
